import { useState, useMemo, useEffect, useCallback } from "react";

import { Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router";

import { AssetState } from "__graphql__/types";
import { AssetInboundedAction } from "analytics/events";
import { HEIGHT as HEADER_HEIGHT } from "app/components/Header";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Button } from "ui/Button/Button";
import { DropDownOption } from "ui/DropDown/DropDownList";

import { useSuppliersAndModels } from "../hooks/useSuppliersAndModels";
import {
  useCreateAssetMutation,
  useTransferAssetMutation,
  useUpdateAssetStateMutation,
} from "../queries/assets.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import { ASSET_TYPE, SupplierName } from "../utils/constants";
import { BatteryRegistrationCard } from "./cards/BatteryRegistrationCard";
import { ConfirmNewBatteryModal } from "./modals/ConfirmNewBatteryModal";

export function NewBatteryRegistration({ serialNumber }: { serialNumber: string }) {
  const intl = useIntl();
  const location = useLocation();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const navigate = useNavigate();
  const { suppliers, modelsBySupplier } = useSuppliersAndModels();
  const {
    countedAssets,
    searchedAsset,
    matchedSuppliersFromScan,
    shouldCreateAsset,
    shouldTransferAsset,
    shouldUpdateAsset,
    currentTaskId,
    countSessionId,
    setCountedAssets,
    resetMutationFlags,
  } = useAssetManagementStore();

  const [createAsset, { loading: isCreatingAsset }] = useCreateAssetMutation({
    onError: (error) => {
      showToastUI({
        id: "battery_create_error",
        description:
          error.message || "pages.inventory.battery-management-page.battery-created-error",
        status: "error",
      });
    },
    onCompleted: (data) => {
      showToastUI({
        id: "battery_created_successfully",
        title: "pages.inventory.battery-management-page.battery-created-successfully",
        status: "success",
      });
      if (data.createAsset?.asset && location.state?.from === routes.inventory.batteryCount.scan) {
        setCountedAssets([data.createAsset.asset as any, ...countedAssets]);
      }
    },
  });

  const [transferAsset, { loading: isTransferringAsset }] = useTransferAssetMutation({
    onError: (error) => {
      showToastUI({
        id: "battery_transfer_error",
        description:
          error.message || "pages.inventory.battery-management-page.battery-transferred-error",
        status: "error",
      });
    },
    onCompleted: (data) => {
      showToastUI({
        id: "battery_transferred_successfully",
        title: "pages.inventory.battery-management-page.battery-transferred-successfully",
        status: "success",
      });
      if (
        data.transferAsset?.asset &&
        location.state?.from === routes.inventory.batteryCount.scan
      ) {
        setCountedAssets([data.transferAsset.asset as any, ...countedAssets]);
      }
    },
  });

  const [updateAssetState, { loading: isUpdatingAssetState }] = useUpdateAssetStateMutation({
    onError: (error) => {
      showToastUI({
        id: "battery_state_update_error",
        description:
          error.message || "pages.inventory.battery-management-page.battery-state-updated-error",
        status: "error",
      });
    },
    onCompleted: (data) => {
      showToastUI({
        id: "battery_state_updated_successfully",
        title: "pages.inventory.battery-management-page.battery-state-updated-successfully",
        status: "success",
      });
      if (
        data.updateAssetState?.asset &&
        location.state?.from === routes.inventory.batteryCount.scan
      ) {
        setCountedAssets([data.updateAssetState.asset as any, ...countedAssets]);
      }
    },
  });

  const [selectedBrand, setSelectedBrand] = useState<DropDownOption | null>(null);
  const [selectedModel, setSelectedModel] = useState<DropDownOption | null>(null);

  const {
    isOpen: isConfirmNewBatteryModalOpen,
    onClose: closeConfirmNewBatteryModal,
    onToggle: openConfirmNewBatteryModal,
  } = useDisclosure();

  const brandOptions = useMemo(() => {
    if (searchedAsset !== null) {
      return [
        {
          key: searchedAsset.supplier?.id as string,
          label: searchedAsset.supplier?.name as string,
        },
      ];
    }
    return suppliers
      .filter((supplier) => matchedSuppliersFromScan.includes(supplier.name as SupplierName))
      .map((supplier) => ({
        key: supplier.id,
        label: supplier.name,
      }));
  }, [searchedAsset, suppliers, matchedSuppliersFromScan]);

  const modelOptions = useMemo(() => {
    if (!selectedBrand) return [];
    if (searchedAsset !== null) {
      return [
        {
          key: searchedAsset.model?.id as string,
          label: searchedAsset.model?.name as string,
        },
      ];
    }

    const models = modelsBySupplier[selectedBrand.key]?.models || [];
    return models.map((model) => ({
      key: model.modelId,
      label: model.name,
    }));
  }, [searchedAsset, selectedBrand, modelsBySupplier]);

  const isFromBatteryCountFlow = location.state?.from === routes.inventory.batteryCount.scan;

  const trackAssetInbounded = (
    assetId: string,
    supplier: string,
    model: string,
    previousState: string | null,
    action: AssetInboundedAction,
  ) => {
    if (!isFromBatteryCountFlow) return;
    sendSegmentTrackEvent("assetInbounded", {
      asset_type: ASSET_TYPE,
      asset_count_process_id: currentTaskId ?? "",
      asset_count_session_id: countSessionId ?? "",
      flow_type: "battery_count",
      asset_id: assetId,
      supplier,
      model,
      previous_state: previousState,
      action,
    });
  };

  const handleCreateBattery = async () => {
    if (!selectedBrand || !selectedModel) return;

    const { data } = await createAsset({
      variables: {
        input: {
          serialNumber,
          supplierId: selectedBrand.key,
          modelId: selectedModel.key,
        },
      },
    });
    if (data?.createAsset?.asset) {
      trackAssetInbounded(
        data.createAsset.asset.id,
        selectedBrand.label,
        selectedModel.label,
        null,
        AssetInboundedAction.Create,
      );
    }
    closeConfirmNewBatteryModal();
    resetMutationFlags();
    navigate(-1);
  };

  const handleTransferBattery = async () => {
    if (!searchedAsset) return;

    const { data } = await transferAsset({
      variables: {
        input: {
          assetId: searchedAsset?.id,
          fromHubCode: searchedAsset?.hubCode,
        },
      },
    });
    if (data?.transferAsset?.asset) {
      trackAssetInbounded(
        searchedAsset.id,
        searchedAsset.supplier?.name ?? "",
        searchedAsset.model?.name ?? "",
        searchedAsset.state,
        AssetInboundedAction.Transfer,
      );
    }
    resetMutationFlags();
    navigate(-1);
  };

  const handleUpdateStateBattery = async () => {
    if (!searchedAsset) return;

    const { data } = await updateAssetState({
      variables: {
        input: {
          assetId: searchedAsset?.id as string,
          newState: AssetState.asset_state_operational,
        },
      },
    });
    if (data?.updateAssetState?.asset) {
      trackAssetInbounded(
        searchedAsset.id,
        searchedAsset.supplier?.name ?? "",
        searchedAsset.model?.name ?? "",
        searchedAsset.state,
        AssetInboundedAction.UpdateState,
      );
    }
    resetMutationFlags();
    navigate(-1);
  };

  const handleMutation = () => {
    if (shouldCreateAsset) {
      handleCreateBattery();
    } else if (shouldTransferAsset) {
      handleTransferBattery();
    } else if (shouldUpdateAsset) {
      handleUpdateStateBattery();
    }
  };

  const handleConfirmBattery = () => {
    if (modelOptions.length > 1) {
      openConfirmNewBatteryModal();
    } else {
      handleMutation();
    }
  };

  const isLoading = isCreatingAsset || isTransferringAsset || isUpdatingAssetState;

  const selectBrand = useCallback(
    (brandId: string) => setSelectedBrand(brandOptions.find((b) => b.key === brandId) || null),
    [brandOptions],
  );

  const selectModel = useCallback(
    (modelId: string) => setSelectedModel(modelOptions.find((m) => m.key === modelId) || null),
    [modelOptions],
  );

  useEffect(() => {
    if (selectedModel === null && modelOptions.length === 1) {
      setSelectedModel(modelOptions[0]);
    }
  }, [modelOptions, selectedModel]);

  useEffect(() => {
    if (selectedBrand === null && brandOptions.length === 1) {
      setSelectedBrand(brandOptions[0]);
    }
  }, [brandOptions, selectedBrand]);

  return (
    <Flex
      position="relative"
      w="full"
      h={`calc(100vh - ${HEADER_HEIGHT})`}
      py="s200"
      direction="column"
      bg="grey.150"
    >
      <Flex h="full" px="s200" gap="s200" direction="column" justifyContent="space-between">
        <BatteryRegistrationCard
          serialNumber={serialNumber}
          brandOptions={brandOptions}
          selectedBrand={selectedBrand}
          setSelectedBrand={selectBrand}
          modelOptions={modelOptions}
          selectedModel={selectedModel}
          setSelectedModel={selectModel}
        />
        <Button
          disabled={!selectedBrand || !selectedModel || isLoading}
          variant="primary"
          onClick={handleConfirmBattery}
        >
          {intl.formatMessage({ id: "pages.inventory.battery-create-page.cta" })}
        </Button>
      </Flex>
      <ConfirmNewBatteryModal
        isLoading={isLoading}
        isOpen={isConfirmNewBatteryModalOpen}
        onClose={closeConfirmNewBatteryModal}
        onClickSecondary={closeConfirmNewBatteryModal}
        onClickPrimary={handleMutation}
      />
    </Flex>
  );
}
